import classNames from 'classnames';
import React from 'react';
import { ArrayHelpers, FieldArray, FormikProps, getIn, SharedRenderProps } from 'formik';
import FormFooter from './components/form-footer';
import FormHeader from './components/form-header';
import { useFormSettingsContext } from '../../Form.context';
import { fieldClasses, FormFieldSC } from './FormField.styled';
import { FormFieldProps } from './FormField';

export type FieldArrayRenderProps<ValueT extends any> = ArrayHelpers & {
  form: FormikProps<ValueT>;
  name: string;
};

interface FormArrayProps<ValueT extends any> extends Omit<FormFieldProps, 'name' | 'children'> {
  name: string;
  /** Should field array validate the form AFTER array updates/changes? */
  validateOnChange?: boolean;
  children: SharedRenderProps<FieldArrayRenderProps<ValueT>>['children'];
}

const FormArray = <ValueT extends any = any>(props: FormArrayProps<ValueT>) => {
  const {
    children,
    className,
    style,
    error,
    noStyle,
    name,
    label,
    required,
    showFeedback,
    caption,
    showError = true,
    maxLength,
    disabled,
    tooltip,
    validateOnChange,
  } = props;

  const settings = useFormSettingsContext({
    showFeedback,
    disabled,
  });

  const hasHeader = Boolean(label || required || settings.showFeedback || tooltip);

  return (
    <FieldArray name={name} validateOnChange={validateOnChange}>
      {(formProps) => {
        const { form } = formProps;
        const isTouched = getIn(form.touched, name);
        const formError = getIn(form.errors, name);
        const formValue = getIn(form.values, name);

        const hasError = name
          ? Boolean(error) || (formError && typeof formError === 'string' && isTouched)
          : false;
        const finalError = error || formError;
        const fieldStatus = isTouched ? (hasError ? 'error' : 'success') : undefined;

        return (
          <FormFieldSC
            style={style}
            className={classNames(
              fieldClasses.wrapper,
              className,
              fieldStatus ? `${fieldClasses.wrapper}-${fieldStatus}` : undefined,
            )}
            noStyle={noStyle}
            hasHeader={hasHeader}
          >
            {hasHeader ? (
              <FormHeader
                required={required}
                status={fieldStatus}
                label={label}
                showFeedback={showFeedback}
                name={name}
                tooltip={tooltip}
              />
            ) : null}
            <div className={classNames(fieldClasses.content)}>{children?.(formProps)}</div>
            <FormFooter
              caption={caption}
              error={hasError && showError ? finalError : undefined}
              maxLength={maxLength}
              currentLength={formValue?.length}
            />
          </FormFieldSC>
        );
      }}
    </FieldArray>
  );
};

export default FormArray;
